import React, { useMemo, useState, useRef, useEffect } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { Box, Stack } from "@mui/material";
import {
  Order,
  canDeleteOrder,
} from "../../types/order";

import { formatAsDate } from "../../utils/date";
import {
  MoreMenuButton,
  MoreMenuButtonProps,
} from "../../components/MoreMenuButton";
import { useSession } from "../../hooks/useSession";
import FlightIcon from '@mui/icons-material/Flight';

type ProspectCardProps = {
  order: Order;
  handleOrderEditClick: (orderGuid: string) => void;
  handleDeleteOrderClick: (orderGuid: string) => void;
  handleConvertToQuoteClick: (prospectGuid: string) => void;
};

export const ProspectCard: React.FC<ProspectCardProps> = ({
  order,
  handleOrderEditClick,
  handleDeleteOrderClick,
  handleConvertToQuoteClick,
}) => {
  const { user } = useSession();

  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const currentRef = ref.current; // Store ref.current in a variable
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    });
  
    if (currentRef) {
      observer.observe(currentRef);
    }
  
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef); // Use the stored variable
      }
    };
  }, []);

  const menuItems = useMemo(() => {
    const items: MoreMenuButtonProps["menuItems"] = [];
    if (order.orderStatus !== "a") {
      items.push({
        label: "Edit",
        onClick: () => handleOrderEditClick(order.orderGuid),
      });
    }

    if (order.orderStatus === "p") {
      items.push({
        label: "Convert To Quote",
        onClick: () => handleConvertToQuoteClick(order.orderGuid),
      });
    }

    if (user && canDeleteOrder(order, user)) {
      items.push({
        label: "Delete",
        onClick: () => handleDeleteOrderClick(order.orderGuid),
      });
    }

    return items;
  }, [
    handleDeleteOrderClick,
    handleOrderEditClick,
    handleConvertToQuoteClick,
    order,
    user,
  ]);

  return (
    <Card
      ref={ref}
      sx={{
        mb: 2,
        touchAction: "none",
        boxShadow: "0px 1px 2px 0px #1018280D",
        cursor: "grab",
      }}
    >
      {isVisible && (
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            height: user?.isRetailUser ? "210px" : "176px",
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontSize: 18,
                  cursor: order.orderStatus === "a" ? null : "pointer",
                }}
                onClick={() => {
                  if (order.orderStatus !== "a") handleOrderEditClick(order.orderGuid);
                }}
              >
                ID {order.orderIdentifier}
              </Typography>
              <Stack direction="row">
                {order.isAutoPilot && (
                  <FlightIcon color="primary" />
                )}                
                <div style={{ touchAction: "none" }}>
                  {menuItems.length > 0 && (
                    <MoreMenuButton
                      menuItems={menuItems}
                      componentProps={{ sx: { marginTop: -1, marginRight: -1 } }}
                    />
                  )}
                </div>
              </Stack>
            </Stack>
          </Box>
          <Typography noWrap sx={{ fontSize: 12 }} color="text.secondary">
            {order.calculatedVinNumber && `VIN: ${order.calculatedVinNumber}`}
          </Typography>
          <Typography sx={{ mb: 1.5, fontSize: 12 }} color="text.secondary">
            Created: {formatAsDate(order.createdTimestamp)}
          </Typography>
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 12 }}
            >
              {`Buyer: ${order?.buyerLastName ?? ""}`}
            </Typography>
          </Box>
        </CardContent>
      )}
    </Card>
  );
};
